<script lang="ts" setup>
import { computed, inject, onMounted, ref } from 'vue';
import {
  Badge,
  Breadcrumbs,
  BreadcrumbsItem,
  Link,
  Modal,
  Stepper,
  TextContainer,
  TextContainerSpacing,
  TextStyle,
  TextStyleFontSize,
  TextStyleShade,
} from '@app/customer/Components';
import { CheckIcon, ExclamationTriangleIcon, InformationCircleIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { App, type Types } from '@app/shared/types/generated-v2';
import { productDetailsService } from '@app/customer/Services';
import { useI18n } from 'vue-i18n';
import Heading from '@app/panel/Components/Heading/components/Heading.vue';
import { Currency } from '@app/shared/ValueObjects';

type SalesOrderItemViewModel = Types['App.Http.ViewModels.Customer.SalesOrderItemViewModel'];
type ProductDetailType = App.Enums.ProductDetailType;
type Reason = {
  title: string;
  renderedContent: string;
};

const { t } = useI18n();

const props = defineProps<{
  item: SalesOrderItemViewModel;
  quantity: number;
  value: number;
  isVirtualIntegration: boolean;
  mayRemoveItem: boolean;
  disabled: boolean;
  locale: string;
  currencyCode: string;
}>();

const visibleProductDetails = inject<ProductDetailType[]>('visibleProductDetails');

const { isVisible } = productDetailsService(visibleProductDetails);

const emit = defineEmits(['increment', 'decrement', 'remove']);

const returnQuantity = ref(props.value);

function onOrderItemUpdate(value: number) {
  returnQuantity.value = value;
  emit('increment', value);
}

function removeOrderItem() {
  if (confirm(t('customer.order-item:confirm-remove-item'))) {
    emit('remove', props.item);
  }
}

function evaluateReturnQuantity(): void {
  if (!props.isVirtualIntegration) {
    return;
  }

  if (props.value !== 0) {
    return;
  }

  onOrderItemUpdate(1);
}

const shownReason = ref<Reason | null>(null);

function showReason(reason: Reason | null): void {
  if (reason) {
    shownReason.value = reason;
  }
}


function showReasonWithReturnRuleInstruction(): void {
  if (props.item?.returnRuleReturnInstruction) {
    shownReason.value = {
      title: props.item.returnRuleReturnInstruction.title,
      renderedContent: props.item.returnRuleReturnInstruction.renderedContent,
    };
  }
}

onMounted(() => evaluateReturnQuantity());

const additionalItemFeeAsMinorUnit = computed(() => Currency.fromMinorValue(props.item.additionalItemFeeAsMinorUnit * returnQuantity.value).toFormattedString(props.locale, props.currencyCode));
</script>

<template>
  <div
    class="grid grid-cols-10 content-start gap-5 py-8"
  >
    <div
      v-if="!isVirtualIntegration"
      class="col-span-10 self-start bg-white sm:col-span-3"
    >
      <img
        v-if="item.product.imageSource"
        v-fallback-img
        class="mx-auto w-40 flex-none rounded-md object-contain sm:mb-0 sm:max-h-40 sm:w-40"
        :class="{
          'opacity-50': disabled,
          'grayscale': disabled,
        }"
        :src="item.product.imageSource"
        :alt="item.product.name"
        loading="lazy"
      >
    </div>

    <div
      class="col-span-10 self-start"
      :class="{'sm:col-span-7': !isVirtualIntegration}"
    >
      <div
        :class="[
          !isVirtualIntegration
            ? 'flex flex-col space-y-4 sm:flex-row sm:space-y-0'
            : 'grid grid-cols-12 sm:gap-12'
        ]"
      >
        <div
          v-if="!isVirtualIntegration"
          class="flex flex-col space-y-4 sm:flex-row sm:space-x-8 sm:space-y-0"
        >
          <TextContainer :spacing="TextContainerSpacing.Tight">
            <div class="flex flex-col">
              <TextStyle
                :font-size="TextStyleFontSize.Base"
                :color="App.Enums.ColorPalette.Slate"
                strong
              >
                {{ item.productName }}
              </TextStyle>

              <div
                v-if="item.quantity > 0 && item.returnedQuantity > 0 && item.quantity !== item.returnedQuantity"
                class="mt-1 py-1"
              >
                <Badge color="blue">
                  {{ t('customer.order-item:already-returned', {'amount': item.returnedQuantity}) }}
                </Badge>
              </div>

              <div
                v-if="item.additionalItemFeeAsMinorUnit > 0 && returnQuantity > 0"
                class="mt-1 py-1"
              >
                <Badge
                  color="blue"
                  class="cursor-pointer"
                  @click="showReasonWithReturnRuleInstruction"
                >
                  <div class="flex justify-between items-center space-x-2">
                    <span>{{ t('customer.order-item:additional-item-fee', { 'amount': additionalItemFeeAsMinorUnit }) }}</span>

                    <InformationCircleIcon class="size-4" />
                  </div>
                </Badge>
              </div>
            </div>

            <div
              v-if="item.quantity === item.returnedQuantity"
              class="flex items-center space-x-2"
            >
              <Badge color="red">
                {{ t('customer.order-item:already-returned-all-items') }}
              </Badge>
            </div>

            <div
              v-if="!item.isReturnable && item.quantity !== item.returnedQuantity"
              class="flex items-center space-x-2"
            >
              <Badge
                color="red"
                :class="item.returnRuleReturnInstruction ? 'cursor-pointer hover:bg-red-100' : ''"
                @click="showReasonWithReturnRuleInstruction"
              >
                {{ t('customer.order-item:cannot-be-returned') }}
              </Badge>
            </div>

            <div
              v-if="item.quantity > 0 && item.isReturnable && disabled"
              class="flex items-center space-x-2"
            >
              <ExclamationTriangleIcon class="size-5 text-amber-500" />

              <Badge
                color="slate"
                class="hover:!bg-slate-100"
              >
                {{ t('customer.order-item:cannot-be-returned-with-selected-items') }}
                -
                <Link
                  native
                  class="lowercase"
                  @click="showReason({
                    title: t('customer.order-item:split-returns-warning-model:title'),
                    renderedContent: t('customer.order-item:split-returns-warning-model:content'),
                  })"
                >
                  {{ t('customer.global:buttons:read-more') }}
                </Link>
              </Badge>
            </div>

            <TextContainer
              :spacing="TextContainerSpacing.Minimal"
            >
              <div class="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <template
                  v-if="item.product.productType === App.Enums.ProductType.BUNDLE"
                >
                  <Badge color="green">
                    {{ t('customer.order-item:bundle') }}
                  </badge>
                </template>

                <template
                  v-if="item.product.parent !== null && item.product.parent.productType === App.Enums.ProductType.BUNDLE"
                >
                  <Badge color="green">
                    {{ t('customer.order-item:bundle-of', {parent: item.product.parent.name}) }}
                  </badge>
                </template>

                <template
                  v-for="(attribute, index) in item.extensionAttributes"
                  :key="index"
                >
                  <Badge color="slate">
                    <span>{{ attribute.option }}: </span>
                    <span>{{ attribute.value }}</span>
                  </Badge>
                </template>
              </div>
            </TextContainer>

            <Breadcrumbs>
              <Breadcrumbs>
                <BreadcrumbsItem
                  v-if="isVisible(App.Enums.ProductDetailType.SKU)"
                  :name="t('customer.status:return-item:sku')"
                  :value="item.product.sku"
                />
                <BreadcrumbsItem
                  v-if="item.product.barcode && isVisible(App.Enums.ProductDetailType.BARCODE)"
                  :name="t('customer.status:return-item:barcode')"
                  :value="item.product.barcode"
                />
                <BreadcrumbsItem
                  v-if="isVisible(App.Enums.ProductDetailType.PRODUCT_ID)"
                  :name="t('customer.status:return-item:product-id')"
                  :value="item.product.externalId"
                />
                <BreadcrumbsItem
                  v-if="item.product.brand && isVisible(App.Enums.ProductDetailType.BRAND)"
                  :name="t('customer.status:return-item:brand')"
                  :value="item.product.brand"
                />
              </Breadcrumbs>

              <Breadcrumbs>
                <BreadcrumbsItem v-if="isVisible(App.Enums.ProductDetailType.PRICE) || isVisible(App.Enums.ProductDetailType.DISCOUNT)">
                  <div class="space-x-1">
                    <template v-if="isVisible(App.Enums.ProductDetailType.PRICE)">
                      <span class="font-semibold">{{ t('customer.status:return-item:price') }}:</span>
                      <span>{{ item.price.label }}</span>
                    </template>
                    <Badge
                      v-if="item.discount.amount && isVisible(App.Enums.ProductDetailType.DISCOUNT)"
                      color="green"
                    >
                      {{ t('customer.order-item:includes-discount', {discount: item.discount.label}) }}
                    </Badge>
                  </div>
                </BreadcrumbsItem>
              </Breadcrumbs>
            </Breadcrumbs>
          </TextContainer>
        </div>

        <div
          v-if="isVirtualIntegration"
          class="col-span-12 mr-24 w-full sm:col-span-8"
        >
          <slot />
        </div>

        <div
          :class="[
            !isVirtualIntegration
              ? 'flex shrink-0 flex-col items-center space-y-4 sm:my-0 sm:ml-auto sm:items-end'
              : 'col-span-12 flex justify-center sm:col-span-4 sm:justify-end'
          ]"
        >
          <div
            v-if="isVirtualIntegration && mayRemoveItem"
            class="mr-2 mt-6 cursor-pointer p-2.5 text-slate-500 hover:text-slate-700"
            @click="removeOrderItem()"
          >
            <TrashIcon
              class=" size-5"
            />
          </div>

          <div
            v-if="!isVirtualIntegration && item.quantity > 0"
            class="flex space-x-1"
          >
            <CheckIcon class="size-5 text-green-500" />

            <TextStyle>
              {{ t('customer.order-item:ordered-quantity', {quantity}) }}
            </TextStyle>
          </div>

          <div
            v-if="item.isReturnable && item.quantity > 0 && !disabled"
            class="space-y-1 text-center"
            :class="{'mt-6': isVirtualIntegration}"
          >
            <Stepper
              v-model="returnQuantity"
              :min="!isVirtualIntegration ? 0 : 1"
              :max="!isVirtualIntegration ? item.returnableQuantity : 999"
              :label="t('customer.order-item:return-quantity')"
              @update:model-value="onOrderItemUpdate"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!isVirtualIntegration && value > 0"
        class="col-span-10 mt-4 sm:col-span-7 sm:col-start-4"
      >
        <slot />
      </div>
    </div>
  </div>

  <Modal
    :open="shownReason !== null"
    @close="shownReason = null"
  >
    <div class="space-y-4">
      <Heading>{{ shownReason.title }}</Heading>
      <TextContainer :spacing="TextContainerSpacing.Loose">
        <TextStyle :shade="TextStyleShade.Subdued">
          <span v-html="shownReason?.renderedContent" />
        </TextStyle>
      </TextContainer>
    </div>
  </Modal>
</template>
